/**
 * IframeGuard Component — UX guard, NOT a security boundary.
 *
 * Displays a friendly "access via the student portal" screen when the app
 * is loaded outside the expected iframe flow. Real access control lives in:
 *   1. Azure Functions JWT validation (api/src/shared/auth.ts)
 *   2. frame-ancestors CSP (staticwebapp.config.json)
 *   3. Front Door origin/referer policy
 *
 * A determined attacker can always call the API directly with a valid JWT;
 * the JWT validation is what actually protects student data. This component
 * just prevents confused users from loading lessons.bankvaultacademy.org in a
 * standalone browser tab and wondering why nothing works.
 */

import { useState, useEffect, ReactNode } from 'react';
import { isAllowedOrigin, setVerifiedParentOrigin, sendToParent } from '../lib/parentOrigins';

interface IframeGuardProps {
  children: ReactNode;
}

export default function IframeGuard({ children }: IframeGuardProps) {
  const [isValidAccess, setIsValidAccess] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if we're in an iframe
    const isInIframe = window.self !== window.top;

    if (!isInIframe) {
      // Not in an iframe - block access
      console.warn('Direct access blocked - lessons must be accessed through the student portal');
      setIsValidAccess(false);
      setIsLoading(false);
      return;
    }

    // We're in an iframe - verify the parent origin via postMessage handshake
    let handshakeReceived = false;

    const handleMessage = (event: MessageEvent) => {
      // Check if origin is allowed (exact match against whitelist)
      if (!isAllowedOrigin(event.origin)) {
        return;
      }

      // Handle handshake response from parent
      if (event.data.type === 'IFRAME_HANDSHAKE_RESPONSE') {
        handshakeReceived = true;
        setVerifiedParentOrigin(event.origin);
        setIsValidAccess(true);
        setIsLoading(false);
        // Notify parent that iframe is ready
        sendToParent({ type: 'IFRAME_READY' });
      }

      // Also accept personality data as proof of valid parent
      if (event.data.type === 'PERSONALITY_DATA') {
        handshakeReceived = true;
        setVerifiedParentOrigin(event.origin);
        setIsValidAccess(true);
        setIsLoading(false);
        // Notify parent that iframe is ready
        sendToParent({ type: 'IFRAME_READY' });
      }
    };

    window.addEventListener('message', handleMessage);

    // Request handshake from the parent.
    //
    // Preferred path: the parent origin is visible via document.referrer. If
    // it's in our whitelist, send the handshake ONLY to that origin. That
    // avoids spraying handshake requests across every possible whitelist entry
    // and leaks nothing if the referrer is stripped.
    //
    // Fallback: send a single wildcard handshake. The parent can answer, and
    // the message handler above binds to the verified origin for all later
    // outbound messages. We no longer loop over ALLOWED_PARENT_ORIGINS — the
    // message handler pins the origin on first valid response.
    try {
      let refererOrigin: string | null = null;
      try {
        if (document.referrer) {
          refererOrigin = new URL(document.referrer).origin;
        }
      } catch (_) { /* ignore malformed referrer */ }

      if (refererOrigin && isAllowedOrigin(refererOrigin)) {
        window.parent.postMessage({ type: 'IFRAME_HANDSHAKE_REQUEST' }, refererOrigin);
      } else {
        // Send once with wildcard; handler verifies event.origin on the response.
        window.parent.postMessage({ type: 'IFRAME_HANDSHAKE_REQUEST' }, '*');
      }
    } catch (error) {
      // Handshake failed - likely not in iframe
    }

    // Timeout - if no handshake received, deny access
    const timeout = setTimeout(() => {
      if (!handshakeReceived) {
        console.warn('Iframe handshake timeout - parent did not respond');
        setIsValidAccess(false);
        setIsLoading(false);
      }
    }, 3000); // 3 second timeout

    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(timeout);
    };
  }, []);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: '2rem',
        textAlign: 'center',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid #e5e7eb',
          borderTop: '4px solid #3B82F6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p style={{ color: '#6b7280', marginTop: '1rem' }}>
          Verifying access...
        </p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!isValidAccess) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: '2rem',
        textAlign: 'center',
        backgroundColor: '#fef2f2'
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          backgroundColor: '#fee2e2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '1.5rem'
        }}>
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#dc2626"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        </div>
        <h1 style={{
          fontSize: '1.5rem',
          color: '#dc2626',
          marginBottom: '0.5rem',
          fontWeight: '600'
        }}>
          Access Denied
        </h1>
        <h2 style={{
          fontSize: '1.125rem',
          color: '#1f2937',
          marginBottom: '1rem',
          fontWeight: '500'
        }}>
          Lessons Must Be Accessed Through the Student Portal
        </h2>
        <p style={{
          color: '#6b7280',
          maxWidth: '400px',
          lineHeight: '1.6'
        }}>
          You cannot access lessons directly via URL. Please log in to your student dashboard
          at <strong>Bank Vault Academy</strong> and access lessons through your assigned coursework.
        </p>
        <a
          href="https://education.bankvaultacademy.org"
          style={{
            marginTop: '2rem',
            padding: '0.75rem 1.5rem',
            backgroundColor: '#3B82F6',
            color: 'white',
            borderRadius: '0.5rem',
            textDecoration: 'none',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3B82F6'}
        >
          Go to Student Portal
        </a>
      </div>
    );
  }

  // Valid access - render children
  return <>{children}</>;
}
